import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import { washHub } from 'store/slices';

import { days } from 'constants/weekdays';

import ObjectID from 'bson-objectid';

import { RefType, SiteModalProps } from './SiteModal.props';

import { Site } from 'types/site';
import { useUpdateSite } from 'hooks/sites/useUpdateSite';
import { useCreateSite } from 'hooks/sites/useCreateSites';
import SiteModalView from './SiteModal.view';

const Container = forwardRef<RefType, SiteModalProps>((_, ref) => {
  const [siteId, setSiteId] = useState('');
  const [site, setSite] = useState<Site | null>(null);

  const washHubId = washHub.selectors.useSelectedWashHubId();

  const { isOpen, onOpen, onClose: _onClose } = useDisclosure();
  const onClose = () => {
    _onClose();
    setSiteId('');
    setSite(null);
  };

  const { onCreateSite, isCreateLoading } = useCreateSite(washHubId, onClose);
  const { onUpdateSite, isEdiLoading } = useUpdateSite(washHubId, onClose);

  const isSiteEdit = Boolean(site);

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (_site?: Site | null) => {
        if (_site) {
          const portal: any =
            typeof (_site as any)?.portal === 'object' &&
            'isDisabled' in (_site as any)?.portal
              ? !(_site as any)?.portal?.isDisabled
              : _site?.portal;

          if (_site) {
            _site = {
              ..._site,
              portal: Boolean(portal),
              logo: portal?.logo || '',
              banner: portal?.banner || '',
            };
            setSite(_site);
            setSiteId(_site._id);
          }
        } else {
          const newObjectId = new ObjectID().toHexString();

          setSiteId(newObjectId);
        }

        onOpen();
      },
    }),
    [onOpen],
  );

  const completeEmptyPayloadFields = (payload: any) => {
    const capacity = Number(payload?.capacity || '0');

    const editPortalName =
      payload?.portal === true ? payload?.portalName : `${siteId}`;
    const createPortalName =
      payload?.portal === true ? payload?.portalName : ``;

    const portalName = !isSiteEdit ? createPortalName : editPortalName;

    /**
     * Because the daily state should have a deliveryDayOfTheWeek sate as every day.
     */
    const deliveryDayOfTheWeek =
      payload?.deliveryFrequency === 'Daily'
        ? (payload.deliveryDayOfTheWeek = days)
        : payload.deliveryDayOfTheWeek;

    const zip = payload?.address?.zip || null;

    payload = {
      ...payload,
      _id: site?._id,
      deliveryDayOfTheWeek,
      zip,
      capacity,
      washHub: payload?.washHub || washHubId,
      portal: payload?.portal,
      portalName: portalName,
      id: payload?._id || siteId,
      logo: payload?.logo || '',
      banner: payload?.banner || '',
      invoice: payload?.invoice,
      qrLandingUrl: payload?.qrLandingUrl || '',
    };

    return payload;
  };

  const onSubmit = (payload: any) => {
    delete payload._isUploading;

    const sanitizedPayload = completeEmptyPayloadFields(payload);

    if (isSiteEdit) {
      onUpdateSite(sanitizedPayload);
    } else {
      onCreateSite(sanitizedPayload);
    }
  };

  const isLoading = isSiteEdit ? isEdiLoading : isCreateLoading;

  const _props = {
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    siteId,
    isSiteEdit,
    site,
    washHubId,
  };

  return <SiteModalView {..._props} />;
});

const AddSiteModal = memo(Container);

export default AddSiteModal;
